import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchCommentThreads } from './useFetchCommentThreads';
import { CommentListItem } from './CommentListItem';
import { CommentListSearchBar } from './CommentListSearchBar';
import { CommentListFilterControls } from './CommentListFilterControls';
import type { FilterType, SortOrder } from './CommentListFilterControls';
import { useCurrentUserId } from '@framework/auth';
import { Loading } from '@framework/ui/atoms/Loading';
import { classNames } from '@framework/utils';
import type { ViewModelId } from 'balus-common';
import { useAtomValue } from 'jotai';
import { viewModelPageContentAtom } from '@user/pages/ViewModelPage/viewModelPageContentAtom';
import { atom } from 'jotai/vanilla';
import { ViewCollection } from '@view-model/domain/view/ViewCollection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { ModelCommentEntity, ModelCommentThreadContainer } from '@view-model/models/sticky/ModelComment';

type CommentListEntry = {
  key: string;
  threadContainer: ModelCommentThreadContainer;
  /** 返信コメントがヒットした場合に設定される。未設定ならプライマリコメントを表示する。 */
  displayComment?: ModelCommentEntity;
};

type Props = {
  viewModelId: ViewModelId;
  onClose: () => void;
};

export const CommentList: React.FC<Props> = ({ viewModelId, onClose }: Props) => {
  const { t } = useTranslation();
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const currentUserId = useCurrentUserId();
  const viewsAtom = useMemo(
    () =>
      atom((get) => {
        return get(viewModelPageContentAtom).viewContentsRecord[viewModelId]?.getViews() ?? ViewCollection.buildEmpty();
      }),
    [viewModelId]
  );
  const views = useAtomValue(viewsAtom);

  const { commentThreads, firstLoading } = useFetchCommentThreads(viewModelId, views);
  const commentListEntries = useMemo((): CommentListEntry[] => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    // filterType を適用
    const filtered = commentThreads.filter((ct) => {
      if (filterType === 'resolved') return ct.modelCommentThread.isResolved();
      if (filterType === 'unread') return currentUserId ? !ct.modelCommentThread.isReadBy(currentUserId) : false;
      return true;
    });

    if (normalizedQuery === '') {
      // 検索なし：スレッド単位で表示
      const entries: CommentListEntry[] = filtered.map((ct) => ({ key: ct.id, threadContainer: ct }));
      return sortOrder === 'oldest' ? entries.slice().reverse() : entries;
    }

    // 検索あり：マッチしたコメント（プライマリ or 返信）単位でエントリを生成
    const entries: (CommentListEntry & { sortMs: number })[] = [];
    for (const ct of filtered) {
      const thread = ct.modelCommentThread;
      const primaryComment = thread.getPrimaryComment();
      if (primaryComment.getBody().toLowerCase().includes(normalizedQuery)) {
        entries.push({ key: ct.id, threadContainer: ct, sortMs: primaryComment.getPostAt().valueOf() });
      }
      // SystemコメントはDB管理用のため検索対象から除外し、Regularコメントのみ対象にする
      for (const reply of thread.getRegularReplyComments()) {
        if (reply.getBody().toLowerCase().includes(normalizedQuery)) {
          entries.push({
            key: `${ct.id}:${reply.id}`,
            threadContainer: ct,
            displayComment: reply,
            sortMs: reply.getPostAt().valueOf(),
          });
        }
      }
    }

    // 投稿日時の新しい順にソート
    entries.sort((a, b) => b.sortMs - a.sortMs);
    return sortOrder === 'oldest' ? entries.slice().reverse() : entries;
  }, [commentThreads, filterType, sortOrder, currentUserId, searchQuery]);
  const className = 'bg-white pointer-events-auto rounded-lg';

  const filterControls = (
    <CommentListFilterControls
      filterType={filterType}
      onFilterChange={setFilterType}
      sortOrder={sortOrder}
      onSortChange={setSortOrder}
    />
  );

  const header = (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-base font-bold">{t('viewModel.commentList.title')}</span>
      <button
        type="button"
        aria-label={t('common.action.close')}
        onClick={onClose}
        className="flex size-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100"
      >
        <FontAwesomeIcon icon={faXmark} aria-hidden />
      </button>
    </div>
  );

  if (firstLoading) {
    return (
      <div className={classNames(className, 'w-[280px]')}>
        {header}
        <CommentListSearchBar value={searchQuery} onChange={setSearchQuery} />
        {filterControls}
        <div className="p-4">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(className, 'flex max-h-full w-[280px] flex-col overflow-hidden')}>
      {header}
      <CommentListSearchBar value={searchQuery} onChange={setSearchQuery} />
      {filterControls}
      {commentListEntries.length === 0 ? (
        <div className="p-4">{t('viewModel.commentList.empty')}</div>
      ) : (
        <div className="overflow-y-scroll [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
          {commentListEntries.map((entry) => {
            const view = views.findById(entry.threadContainer.viewId);
            if (!view) return null;

            return (
              <CommentListItem
                key={entry.key}
                commentThread={entry.threadContainer.modelCommentThread}
                view={view}
                displayComment={entry.displayComment}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
